import { useCallback, useState } from 'react'

import { createHighlight, type Highlight, type HighlightPositionData } from '@/api/services/highlight'

import { HighlightColor } from '../highlight/highlight-colors'
import { useHighlightCrud } from './useHighlightCrud'

// 型定義
export type PendingSelection = {
  page: number
  rect: { x: number; y: number; width: number; height: number }
}

export type HighlightStartPoint = {
  page: number
  x: number
  y: number
}

type UseImageHighlightParams = {
  episodeId?: number
}

type UseImageHighlightReturn = {
  // 状態
  highlights: Highlight[]
  highlightListOpen: boolean
  highlightMemoOpen: boolean
  editingHighlight: Highlight | null
  pendingSelection: PendingSelection | null
  isSelectionMode: boolean
  highlightStartPoint: HighlightStartPoint | null
  highlightsVisible: boolean

  // UI制御関数
  setHighlights: React.Dispatch<React.SetStateAction<Highlight[]>>
  setHighlightListOpen: (open: boolean) => void
  setHighlightMemoOpen: (open: boolean) => void
  setEditingHighlight: (editing: Highlight | null) => void
  setPendingSelection: (pending: PendingSelection | null) => void
  setIsSelectionMode: (mode: boolean) => void
  setHighlightStartPoint: (point: HighlightStartPoint | null) => void
  toggleHighlightsVisible: () => void

  // ハイライト操作
  handleCreateHighlight: (color: HighlightColor, note: string) => Promise<void>
  handleUpdateHighlight: (id: number, color: HighlightColor, note: string) => Promise<void>
  handleDeleteHighlight: (id: number) => Promise<void>
  handleHighlightClick: (highlightId: number) => void
  handleCancelSelection: () => void
  handleImageInteraction: (e: React.MouseEvent | React.TouchEvent, slideIndex: number) => void
  handleOpenHighlightList: () => void
  handleHighlightNavigate: (
    highlightId: number,
    splideRef: React.RefObject<{ go: (index: number) => void } | null>,
    setCurrentIndex: (index: number) => void,
  ) => void
  startSelectionMode: () => void
}

export function useImageHighlight({ episodeId }: UseImageHighlightParams): UseImageHighlightReturn {
  // ハイライトCRUD (共通hook)
  const {
    highlights,
    setHighlights,
    handleUpdateHighlight: crudUpdate,
    handleDeleteHighlight: crudDelete,
  } = useHighlightCrud({ episodeId })

  // ハイライト状態
  const [highlightListOpen, setHighlightListOpen] = useState(false)
  const [editingHighlight, setEditingHighlight] = useState<Highlight | null>(null)
  const [highlightMemoOpen, setHighlightMemoOpen] = useState(false)
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null)
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  // 2段階選択用: 開始点を保存
  const [highlightStartPoint, setHighlightStartPoint] = useState<HighlightStartPoint | null>(null)
  // ハイライト表示/非表示
  const [highlightsVisible, setHighlightsVisible] = useState(true)

  // ハイライト表示トグル
  const toggleHighlightsVisible = useCallback(() => {
    setHighlightsVisible((prev) => !prev)
  }, [])

  // ハイライト作成
  const handleCreateHighlight = useCallback(
    async (color: HighlightColor, note: string) => {
      if (!episodeId || !pendingSelection) return

      const positionData: HighlightPositionData = {
        selections: [
          {
            page: pendingSelection.page,
            rect: pendingSelection.rect,
          },
        ],
      }

      try {
        const newHighlight = await createHighlight({
          episode_id: episodeId,
          media_type: 'image',
          position_data: positionData,
          color,
          note: note || undefined,
        })
        setHighlights((prev) => [...prev, newHighlight])
        setHighlightMemoOpen(false)
        setPendingSelection(null)
      } catch (error) {
        console.error('ハイライト作成エラー:', error)
      }
    },
    [episodeId, pendingSelection, setHighlights],
  )

  // 2段階選択: 画像クリック/タップ時の処理
  const handleImageInteraction = useCallback(
    (e: React.MouseEvent | React.TouchEvent, slideIndex: number) => {
      if (!episodeId || !isSelectionMode) return

      e.preventDefault()
      e.stopPropagation()

      const target = e.currentTarget as HTMLElement
      const rect = target.getBoundingClientRect()

      let clientX: number
      let clientY: number

      if ('touches' in e) {
        if (e.type === 'touchend' && e.changedTouches.length > 0) {
          clientX = e.changedTouches[0].clientX
          clientY = e.changedTouches[0].clientY
        } else if (e.touches.length > 0) {
          clientX = e.touches[0].clientX
          clientY = e.touches[0].clientY
        } else {
          return
        }
      } else {
        clientX = e.clientX
        clientY = e.clientY
      }

      const x = ((clientX - rect.left) / rect.width) * 100
      const y = ((clientY - rect.top) / rect.height) * 100

      if (!highlightStartPoint) {
        // 開始点を設定
        setHighlightStartPoint({ page: slideIndex, x, y })
        if (navigator.vibrate) {
          navigator.vibrate(50)
        }
      } else {
        // 終了点で範囲確定
        const startX = highlightStartPoint.x
        const startY = highlightStartPoint.y

        const highlightRect = {
          x: Math.min(startX, x),
          y: Math.min(startY, y),
          width: Math.abs(x - startX),
          height: Math.abs(y - startY),
        }

        // 範囲が小さすぎる場合はキャンセル
        if (Math.abs(x - startX) < 2 || Math.abs(y - startY) < 2) {
          setHighlightStartPoint(null)
          return
        }

        setPendingSelection({ page: highlightStartPoint.page, rect: highlightRect })
        setHighlightMemoOpen(true)
        setHighlightStartPoint(null)
        setIsSelectionMode(false)
      }
    },
    [episodeId, isSelectionMode, highlightStartPoint],
  )

  // 選択キャンセル
  const handleCancelSelection = useCallback(() => {
    setIsSelectionMode(false)
    setHighlightStartPoint(null)
  }, [])

  // ハイライトをクリックして編集
  const handleHighlightClick = useCallback(
    (highlightId: number) => {
      const highlight = highlights.find((h) => h.id === highlightId)
      if (highlight) {
        setEditingHighlight(highlight)
      }
    },
    [highlights],
  )

  // ハイライト更新
  const handleUpdateHighlight = async (id: number, color: HighlightColor, note: string) => {
    await crudUpdate(id, color, note)
    setEditingHighlight(null)
  }

  // ハイライト削除
  const handleDeleteHighlight = async (id: number) => {
    await crudDelete(id)
    setEditingHighlight(null)
  }

  // ハイライト一覧を開く
  const handleOpenHighlightList = useCallback(() => {
    setHighlightListOpen(true)
  }, [])

  // ページ遷移（ハイライト一覧から）
  const handleHighlightNavigate = useCallback(
    (
      highlightId: number,
      splideRef: React.RefObject<{ go: (index: number) => void } | null>,
      setCurrentIndex: (index: number) => void,
    ) => {
      // ハイライトIDからページ番号を取得
      const highlight = highlights.find((h) => h.id === highlightId)
      if (!highlight) return

      const page = highlight.position_data.selections?.[0]?.page
      if (page === undefined) return

      if (splideRef.current) {
        splideRef.current.go(page)
        setCurrentIndex(page)
      }
    },
    [highlights],
  )

  // 選択モードを開始
  const startSelectionMode = useCallback(() => {
    setIsSelectionMode(true)
    setHighlightStartPoint(null)
  }, [])

  return {
    // 状態
    highlights,
    highlightListOpen,
    highlightMemoOpen,
    editingHighlight,
    pendingSelection,
    isSelectionMode,
    highlightStartPoint,
    highlightsVisible,

    // UI制御関数
    setHighlights,
    setHighlightListOpen,
    setHighlightMemoOpen,
    setEditingHighlight,
    setPendingSelection,
    setIsSelectionMode,
    setHighlightStartPoint,
    toggleHighlightsVisible,

    // ハイライト操作
    handleCreateHighlight,
    handleUpdateHighlight,
    handleDeleteHighlight,
    handleHighlightClick,
    handleCancelSelection,
    handleImageInteraction,
    handleOpenHighlightList,
    handleHighlightNavigate,
    startSelectionMode,
  }
}
